import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';

import FilterSearch from 'components/FilterSearch';
import FilterDropdown from 'components/FilterDropdown';

import { DELETE, GET, httpOk } from 'helpers/api';
import { DOWNLOAD_FILE, formatDate, formatMoney } from 'helpers/helpers';

import { ReactComponent as WarehouseIcon } from 'assets/icons/sidebar/warehouse.svg';
import { ReactComponent as DownloadIcon } from 'assets/icons/download.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';

import CreateRecipe from './modals/CreateRecipe';
import CreateBox from './modals/CreateBox';
import CreateVariants from './modals/CreateVariants';
import ConfirmModal from 'components/ConfirmModal';
import Pagination from 'components/Pagination';

const initFilterData = {
    page: 1,
    page_size: 10,
    search: '',
    status: '',
}

function Production() {
    const { t } = useTranslation()

    const [dropdown, setDropdown] = useState(false)
    const [createRecipeModal, setCreateRecipeModal] = useState({ show: false, id: '' })
    const [createBoxModal, setCreateBoxModal] = useState({ show: false, id: '' })
    const [createVariantsModal, setCreateVariantsModal] = useState({ show: false, id: '' })

    const [confirmModal, setConfirmModal] = useState({ show: false, id: '' })

    const [filterData, setFilterData] = useState(initFilterData)

    const [data, setData] = useState({})

    async function exportFile() {
        await DOWNLOAD_FILE('/recipes/export/', filterData, t('bom'))
    }

    async function deleteRecipe() {
        const response = await DELETE(`/recipes/${confirmModal.id}/`)
        if (httpOk(response)) {
            setConfirmModal({ show: false })
            getData()
        }
    }

    const handlePaginate = (args) => {
        setFilterData(prev => ({
            ...prev,
            page: args.page + 1,
            page_size: args.page_size
        }));
    };

    const getData = useCallback(async () => {
        const [response, responseStats] = await Promise.all([
            GET('/recipes/dashboard/', filterData),
            GET('/recipes/', filterData),
        ]);

        if (httpOk(response)) {
            setData({ ...response.data, ...responseStats.data, });
        }
    }, [filterData]);

    useEffect(() => {
        getData();
    }, [getData]);


    return (
        <>
            <div className="d-between">
                <div>
                    <div className="main-content-title">
                        {t('bom')}
                    </div>

                    <div className="main-content-subtitle">
                        {t('product_composition_management')}
                    </div>
                </div>

                <div className="d-flex gap-8">
                    <button className="dark gap-16" onClick={() => setCreateRecipeModal({ show: true })}>
                        <PlusIcon className="small" />
                        {t('create_recipe')}
                    </button>
                </div>
            </div>


            <div className="grid4 gap-16">
                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('active_recipes')}
                            </div>
                            <div className="value text-success">
                                {formatMoney(data?.active_recipes)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('in_production')}
                            </div>
                            <div className="value text-purple">
                                {formatMoney(data?.in_production)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('require_attention')}
                            </div>
                            <div className="value text-warning">
                                {formatMoney(data?.attention_recipes)}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="d-between">
                        <div>
                            <div className="title">
                                {t('average_composition')}
                            </div>
                            <div className="value">
                                <div className="d-flex align-items-center gap-8">
                                    {data?.average_ingredients}
                                    <div className="fz12 text-second">
                                        {t('ingredients')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card">
                <div className="d-flex gap-16 w-100">
                    <FilterSearch
                        value={filterData.search}
                        onChange={(e) => setFilterData({ ...filterData, search: e.target.value })}
                        placeholder={t('search_by_sku_or_name')}
                    />

                    <FilterDropdown dropdown={dropdown} setDropdown={setDropdown}>
                        {/* <div className="form-group">
                            <label htmlFor="name">{t('raw_material')}</label>
                            <CustomSelect
                                options={rawMaterials}
                                value={rawMaterials.find(option => option.id === filterData?.raw_material_id) || ''}
                                onChange={(option) => setFilterData({ ...filterData, raw_material_id: option?.id })}
                                getOptionLabel={(option) => option.name}
                                getOptionValue={(option) => option.id}

                            />
                        </div> */}
                    </FilterDropdown>
                    <button className="white third" onClick={() => exportFile()}>
                        <DownloadIcon />
                        {t('export')}
                    </button>
                </div>
            </div>

            {data?.results?.map((item, index) => (
                <div className="card" key={index}>

                    <div className="d-between">
                        <div className="d-flex flex-column gap-8 mb-30">
                            <div className="d-flex align-items-center gap-8">
                                <div className="fz16 text-third">
                                    {item.product_name}
                                </div>
                                <div className="alert alert-dark">
                                    <WarehouseIcon />
                                    {item.type_label}
                                </div>
                            </div>

                            <div className="text-fifth">
                                SKU: {item.sku} • {t('version')}: {item.current_version} • {t('updated')}: {formatDate(item.version_created_at)}
                            </div>

                            <div className="d-flex align-items-center gap-8">
                                <div className="text-second">
                                    {item.type === 'tyle' ?
                                        <>
                                            {item.rows_per_box ? `${item.rows_per_box} ${t('number_of_rows')}` : ''}
                                            {(item.rows_per_box && item.pieces_per_row) && '×'}
                                            {item.pieces_per_row ? `${item.pieces_per_row} ${t('unit_short')}` : ''}
                                            = {item.rows_per_box * item.pieces_per_row} {t('pcs_in_box')}
                                        </>
                                        :
                                        <>
                                            ≈{item.approx_pieces_per_box} {t('pcs_in_box')}
                                        </>
                                    }
                                </div>
                                <div className="alert alert-white">
                                    {t('calculation')}: {item.calc_basis_label}
                                </div>
                            </div>
                        </div>

                        <div className="d-flex gap-8">
                            {item.type === 'tile' &&
                                <button className="white gap-16" onClick={() => setCreateVariantsModal({ show: true, id: item.id })}>
                                    {t('Создать разновидность')}
                                </button>
                            }

                            <button className="white gap-16" onClick={() => setCreateRecipeModal({ show: true, id: item.id })}>
                                <EditIcon className="small" />
                                {t('edit_recipe')}
                            </button>

                            <button className="white gap-16" onClick={() => setConfirmModal({ show: true, id: item.id })}>
                                <DeleteIcon className="small" />
                                {t('delete')}
                            </button>
                        </div>
                    </div>

                    <div className="text-second mb-16">
                        {t('technologist')}: {item.technologist_name}
                    </div>

                    <div className="text-second mb-12">
                        {t('composition')} (на 1 {item.product_unit_short_name}):
                    </div>

                    {item?.ingredients?.length > 0 &&
                        <table className="table">
                            <thead>
                                <tr>
                                    <th style={{ width: '20%' }}>{t('raw_material')}</th>
                                    <th style={{ width: '20%' }}>{t('quantity')}</th>
                                    <th style={{ width: '20%' }}>{t('unit_short')}</th>
                                    <th style={{ width: '20%' }}>{t('balance')}</th>
                                </tr>
                            </thead>

                            <tbody>
                                {item.ingredients?.map((item, index) => (
                                    <tr key={index}>
                                        <td>{item.raw_material_name}</td>
                                        <td>{(item.quantity)}</td>
                                        <td>{item.unit_short_name}</td>
                                        <td className={item.is_deficit ? "text-danger" : ""}>
                                            {formatMoney(item.current_stock, 2)} {item.unit_short_name}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    }

                    {item?.semi_finished_components?.length > 0 &&
                        <table className="table">
                            <thead>
                                <tr>
                                    <th style={{ width: '20%' }}>{t('product')}</th>
                                    <th style={{ width: '20%' }}>{t('percentage')} %</th>
                                    <th style={{ width: '20%' }}>{t('unit_short')}</th>
                                    <th style={{ width: '20%' }}>{t('balance')}</th>
                                </tr>
                            </thead>

                            <tbody>
                                {item.semi_finished_components?.map((item, index) => (
                                    <tr key={index}>
                                        <td>{item.product_name}</td>
                                        <td>{(item.percentage)}</td>
                                        <td>{item.unit_short_name}</td>
                                        <td className={item.is_deficit ? "text-danger" : ""}>
                                            {formatMoney(item.current_stock, 2)} {item.unit_short_name}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    }

                    <div className="card-info">
                        <div className="text-second">
                            {t('total_consumption_per_100kg')}:
                        </div>
                        {formatMoney(item.total_consumption_per_unit)} {t('kg_of_raw_material')}
                    </div>


                    {item?.variants?.length > 0 &&
                        <>
                            <hr />

                            <table className="table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '25%' }}>{t('variant')}</th>
                                        <th style={{ width: '25%' }}>{t('weight_grams')}</th>
                                        <th style={{ width: '25%' }}>{t('weight_percent')}</th>
                                        <th style={{ width: '25%' }}>{t('total_consumption_per_unit')}</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {item.variants?.map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.name}</td>
                                            <td>{formatMoney(item.weight_grams)}</td>
                                            <td>{item.weight_percent} %</td>
                                            <td>{item.total_consumption_per_unit}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    }

                </div>
            ))}

            <Pagination
                paginate={handlePaginate}
                pagination={{
                    total: data?.count || 0
                }}
                defaultSize={10}
            />

            <CreateRecipe modal={createRecipeModal.show} setModal={() => setCreateRecipeModal({ show: false })} getData={getData} id={createRecipeModal.id} />
            <CreateBox modal={createBoxModal.show} setModal={() => setCreateBoxModal({ show: false })} getData={getData} id={createBoxModal.id} />
            <CreateVariants modal={createVariantsModal.show} setModal={() => setCreateVariantsModal({ show: false })} getData={getData} id={createVariantsModal.id} />

            <ConfirmModal modal={confirmModal.show} setModal={() => setConfirmModal({ show: false })} confirm={deleteRecipe} />
        </>
    )
}

export default Production